import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '../../lib/utils';

const THEMES = { light: '', dark: '.dark' };

const ChartContext = React.createContext(null);

function useChart() {
    const context = React.useContext(ChartContext);
    if (!context) {
        throw new Error('useChart must be used within a <ChartContainer />');
    }
    return context;
}

const ChartContainer = React.forwardRef(({ id, className, children, config, ...props }, ref) => {
    const uniqueId = React.useId();
    const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

    return (
        <ChartContext.Provider value={{ config }}>
            <div
                data-chart={chartId}
                ref={ref}
                className={cn(
                    "flex justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-slate-400 [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-slate-200/80 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-slate-200 [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-slate-200 [&_.recharts-radial-bar-background-sector]:fill-slate-100 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-slate-100 [&_.recharts-reference-line_[stroke='#ccc']]:stroke-slate-200 [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
                    className,
                )}
                {...props}
            >
                <ChartStyle id={chartId} config={config} />
                <RechartsPrimitive.ResponsiveContainer>
                    {children}
                </RechartsPrimitive.ResponsiveContainer>
            </div>
        </ChartContext.Provider>
    );
});
ChartContainer.displayName = 'ChartContainer';

const ChartStyle = ({ id, config }) => {
    const colorConfig = Object.entries(config || {}).filter(
        ([, item]) => item?.theme || item?.color,
    );

    if (!colorConfig.length) return null;

    return (
        <style
            dangerouslySetInnerHTML={{
                __html: Object.entries(THEMES)
                    .map(([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
        .map(([key, itemConfig]) => {
            const color = itemConfig.theme?.[theme] || itemConfig.color;
            return color ? `  --color-${key}: ${color};` : null;
        })
        .filter(Boolean)
        .join('\n')}
}
`)
                    .join('\n'),
            }}
        />
    );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const ChartTooltipContent = React.forwardRef(({
    active,
    payload,
    className,
    indicator = 'dot',
    hideLabel = false,
    hideIndicator = false,
    label,
    labelFormatter,
    labelClassName,
    formatter,
    color,
    nameKey,
    labelKey,
}, ref) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
        if (hideLabel || !payload?.length) return null;

        const [item] = payload;
        const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        const value = !labelKey && typeof label === 'string'
            ? config[label]?.label || label
            : itemConfig?.label;

        if (labelFormatter) {
            return (
                <div className={cn('font-medium', labelClassName)}>
                    {labelFormatter(value, payload)}
                </div>
            );
        }

        if (!value) return null;

        return <div className={cn('font-medium', labelClassName)}>{value}</div>;
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

    if (!active || !payload?.length) return null;

    const nestLabel = payload.length === 1 && indicator !== 'dot';

    return (
        <div
            ref={ref}
            className={cn(
                'grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-slate-200/80 bg-white px-2.5 py-1.5 text-xs shadow-xl',
                className,
            )}
        >
            {!nestLabel ? tooltipLabel : null}
            <div className='grid gap-1.5'>
                {payload
                    .filter((item) => item.type !== 'none')
                    .map((item, index) => {
                        const key = `${nameKey || item.name || item.dataKey || 'value'}`;
                        const itemConfig = getPayloadConfigFromPayload(config, item, key);
                        const indicatorColor = color || item.payload?.fill || item.color;

                        return (
                            <div
                                key={String(item.dataKey || index)}
                                className={cn(
                                    'flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-slate-400',
                                    indicator === 'dot' && 'items-center',
                                )}
                            >
                                {formatter && item?.value !== undefined && item.name ? (
                                    formatter(item.value, item.name, item, index, item.payload)
                                ) : (
                                    <>
                                        {itemConfig?.icon ? (
                                            <itemConfig.icon />
                                        ) : (
                                            !hideIndicator && (
                                                <div
                                                    className={cn(
                                                        'shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]',
                                                        {
                                                            'h-2.5 w-2.5': indicator === 'dot',
                                                            'w-1': indicator === 'line',
                                                            'w-0 border-[1.5px] border-dashed bg-transparent':
                                                                indicator === 'dashed',
                                                            'my-0.5': nestLabel && indicator === 'dashed',
                                                        },
                                                    )}
                                                    style={{
                                                        '--color-bg': indicatorColor,
                                                        '--color-border': indicatorColor,
                                                    }}
                                                />
                                            )
                                        )}
                                        <div
                                            className={cn(
                                                'flex flex-1 justify-between leading-none',
                                                nestLabel ? 'items-end' : 'items-center',
                                            )}
                                        >
                                            <div className='grid gap-1.5'>
                                                {nestLabel ? tooltipLabel : null}
                                                <span className='text-slate-500'>
                                                    {itemConfig?.label || item.name}
                                                </span>
                                            </div>
                                            {item.value != null && (
                                                <span className='font-mono font-medium tabular-nums text-slate-950'>
                                                    {typeof item.value === 'number'
                                                        ? item.value.toLocaleString('es-MX')
                                                        : item.value}
                                                </span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
});
ChartTooltipContent.displayName = 'ChartTooltipContent';

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef(({
    className,
    hideIcon = false,
    payload,
    verticalAlign = 'bottom',
    nameKey,
}, ref) => {
    const { config } = useChart();

    if (!payload?.length) return null;

    return (
        <div
            ref={ref}
            className={cn(
                'flex items-center justify-center gap-4',
                verticalAlign === 'top' ? 'pb-3' : 'pt-3',
                className,
            )}
        >
            {payload
                .filter((item) => item.type !== 'none')
                .map((item) => {
                    const key = `${nameKey || item.dataKey || 'value'}`;
                    const itemConfig = getPayloadConfigFromPayload(config, item, key);

                    return (
                        <div
                            key={String(item.value)}
                            className='flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-slate-400'
                        >
                            {itemConfig?.icon && !hideIcon ? (
                                <itemConfig.icon />
                            ) : (
                                <div
                                    className='h-2 w-2 shrink-0 rounded-full'
                                    style={{ backgroundColor: item.color }}
                                />
                            )}
                            <span className='text-slate-500'>{itemConfig?.label || item.value}</span>
                        </div>
                    );
                })}
        </div>
    );
});
ChartLegendContent.displayName = 'ChartLegendContent';

function getPayloadConfigFromPayload(config, payload, key) {
    if (typeof payload !== 'object' || payload === null) return undefined;

    const payloadPayload = 'payload' in payload
        && typeof payload.payload === 'object'
        && payload.payload !== null
        ? payload.payload
        : undefined;

    let configLabelKey = key;

    if (key in payload && typeof payload[key] === 'string') {
        configLabelKey = payload[key];
    } else if (
        payloadPayload
        && key in payloadPayload
        && typeof payloadPayload[key] === 'string'
    ) {
        configLabelKey = payloadPayload[key];
    }

    return configLabelKey in config ? config[configLabelKey] : config[key];
}

export {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent,
    ChartStyle,
};
